import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, History, Info, Plug, Plus, Users, X } from 'lucide-react';
import type { DialectDescriptor, ProjectFile } from '@bits2schema/shared';
import { api } from '../api/client';
import { CommandStackProvider } from '../command/stack';
import { useT } from '../i18n';
import { createAddTableCommand } from '../schema/commands';
import { createDefaultTable } from '../schema/defaults';
import { SchemaStoreProvider, useSchemaStore } from '../schema/store';
import {
  describeError,
  emptyConnectionDraft,
  isConnectionDraftComplete,
  toConnectionInput,
  type ConnectionDraft,
} from '../utils/format';
import { useEditLease } from '../workspace/useEditLease';
import { Workspace } from '../workspace/Workspace';
import { ConnectionForm } from './ConnectionForm';
import { HistoryPanel } from './HistoryPanel';
import { ImportWarnings } from './ImportWarnings';
import { Modal } from './Modal';
import { ProjectEditorsCard } from './ProjectEditorsCard';
import { ProjectRenameCard } from './ProjectRenameCard';

interface ProjectPageProps {
  projectId: string;
  dialects: DialectDescriptor[];
  onBack: () => void;
}

/**
 * Detail projektu po importu. Stránka je záměrně holá — jen toolbar a canvas
 * přes celou šířku; souhrn, tabulky, správa připojení a varování importu
 * žijí v popupu za tlačítkem "Informace o DB", aby canvasu zůstalo co
 * nejvíc místa.
 */
export function ProjectPage({ projectId, dialects, onBack }: ProjectPageProps) {
  const t = useT();
  const [project, setProject] = useState<ProjectFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [dbInfoOpen, setDbInfoOpen] = useState(false);
  // Epic 14 (E14-US10/US11) — měkký zámek souběžné editace; aktivní po celou dobu, kdy je projekt otevřený.
  const { lease, isMine, takeover, secondsSinceActivity } = useEditLease(projectId);
  const readOnly = lease !== null && !isMine;
  // Revert (E3-US7) přepíše schema i canvas na serveru — vynutí remount Workspace,
  // aby CanvasStoreProvider převzal čerstvý `canvas` místo toho, co má ve svém
  // vlastním stavu z doby před revertem (initialCanvas se čte jen při mountu).
  const [canvasResetToken, setCanvasResetToken] = useState(0);
  // Epic 15, E15-US4 — portal cíl v `.toolbar__actions` pro `CanvasToolsMenu`
  // (`CanvasView.tsx`, hluboko uvnitř `Workspace`) — toolbar leží MIMO strom
  // se schema/canvas store, takže menu tlačítko musí přijít portálem, ne
  // zvednutím providerů nad celou stránku (zdůvodnění v epic-15 kap. 8).
  const [toolsMenuSlot, setToolsMenuSlot] = useState<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    try {
      const { project: opened } = await api.openProject(projectId);
      setProject(opened);
      setError(null);
    } catch (caught) {
      setError(describeError(caught, t));
    }
  }, [projectId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error && !project) {
    return (
      <section className="card">
        <div className="notice notice--error">{error}</div>
        <button type="button" onClick={onBack}>
          <ArrowLeft size={16} />
          {t('project.backToList')}
        </button>
      </section>
    );
  }

  if (!project) {
    return <p className="hint">{t('common.loading')}</p>;
  }

  const dialectLabel =
    dialects.find((item) => item.dialectId === project.dialect)?.label ?? project.dialect;

  return (
    <section>
      <div className="toolbar">
        <h1>{project.name}</h1>
        <div className="toolbar__actions">
          <span className="badge">{dialectLabel}</span>
          <span className="badge">
            {project.origin === 'import'
              ? t('projects.origin.import')
              : t('projects.origin.greenfield')}
          </span>
          <div className="toolbar__tools-slot" ref={setToolsMenuSlot} />
          <button type="button" onClick={() => setDbInfoOpen(true)}>
            <Info size={16} />
            {t('project.dbInfo.button')}
          </button>
          <button type="button" onClick={() => setHistoryOpen(true)}>
            <History size={16} />
            {t('history.openButton')}
          </button>
          <button type="button" onClick={onBack}>
            <ArrowLeft size={16} />
            {t('project.backToList')}
          </button>
        </div>
      </div>

      {error && <div className="notice notice--error">{error}</div>}

      {readOnly && lease && (
        <div className="notice">
          {t('lease.readOnlyBanner', { name: lease.displayName, seconds: secondsSinceActivity ?? 0 })}{' '}
          <button type="button" onClick={() => void takeover()}>
            <Users size={16} />
            {t('lease.takeoverButton')}
          </button>
        </div>
      )}

      {/*
       * Schema store žije tady, nad `Workspace` — kromě canvasu ho čte i
       * obsah popupu "Informace o DB" (souhrn, tabulky, připojení, varování
       * importu), takže by po editaci jinak zůstal zastaralý až do příštího
       * `load()`. Stejný remount trik jako u canvasu (E3-US7): revert
       * přepíše schema i canvas na serveru, `key` vynutí, že provider
       * převezme čerstvý `initialSchema` místo toho, co má ve vlastním
       * stavu z doby před revertem.
       *
       * `CommandStackProvider` (Epic 13) obaluje schema i canvas store
       * (canvas je až uvnitř `Workspace`) — jeden sdílený undo/redo zásobník
       * napříč oběma, ne dva nezávislé. Sdílí `key` s remountem výše: revert
       * i přepnutí projektu tak zásobník automaticky vyprázdní (E13-US5 AC —
       * commandy vztažené k modelu před revertem/přepnutím nesmí zůstat
       * vratné), beze zvláštního volání `clear()`.
       */}
      <CommandStackProvider key={`${project.id}-${canvasResetToken}`}>
        <SchemaStoreProvider projectId={project.id} initialSchema={project.schema}>
          <ProjectWorkspaceSection project={project} readOnly={readOnly} toolsMenuSlot={toolsMenuSlot} />

          {dbInfoOpen && (
            <Modal
              title={t('project.dbInfo.title')}
              onClose={() => setDbInfoOpen(false)}
              wide
              actions={
                <button type="button" onClick={() => setDbInfoOpen(false)}>
                  <X size={16} />
                  {t('common.close')}
                </button>
              }
            >
              <div className="stack">
                <ProjectSummaryCard />
                <ProjectRenameCard project={project} onChanged={load} />
                <ProjectEditorsCard project={project} onChanged={load} />
                <ConnectionCard project={project} onChanged={load} />
                <ProjectTablesCard />
                <div className="card">
                  <h2>{t('warnings.title')}</h2>
                  <ImportWarnings warnings={project.importWarnings} />
                </div>
              </div>
            </Modal>
          )}
        </SchemaStoreProvider>
      </CommandStackProvider>

      {historyOpen && (
        <HistoryPanel
          projectId={project.id}
          onClose={() => setHistoryOpen(false)}
          onReverted={() => {
            setCanvasResetToken((token) => token + 1);
            void load();
          }}
          readOnly={readOnly}
        />
      )}
    </section>
  );
}

function SummaryItem({ label, value }: { label: string; value: number }) {
  return (
    <div className="summary-grid__item">
      <div className="summary-grid__value">{value}</div>
      <div className="summary-grid__label">{label}</div>
    </div>
  );
}

function ProjectSummaryCard() {
  const t = useT();
  const { schema } = useSchemaStore();
  const columnCount = schema.tables.reduce((sum, table) => sum + table.columns.length, 0);
  const relationCount = schema.tables.reduce((sum, table) => sum + table.foreignKeys.length, 0);
  return (
    <div className="card">
      <div className="summary-grid">
        <SummaryItem label={t('project.summary.tables')} value={schema.tables.length} />
        <SummaryItem label={t('project.summary.columns')} value={columnCount} />
        <SummaryItem label={t('project.summary.relations')} value={relationCount} />
        <SummaryItem label={t('project.summary.schemas')} value={schema.schemas.length} />
      </div>
    </div>
  );
}

function ProjectWorkspaceSection({
  project,
  readOnly,
  toolsMenuSlot,
}: {
  project: ProjectFile;
  readOnly: boolean;
  toolsMenuSlot: HTMLDivElement | null;
}) {
  const t = useT();
  const { schema, execute: schemaExecute } = useSchemaStore();
  if (schema.tables.length === 0) {
    const addFirstTable = () => {
      const table = createDefaultTable(schema);
      schemaExecute(createAddTableCommand(table));
    };
    return (
      <div className="card">
        <p className="hint">{t('project.canvasPlaceholder')}</p>
        <button
          type="button"
          className="primary"
          style={{ marginTop: 'var(--space-3)' }}
          disabled={readOnly}
          onClick={addFirstTable}
        >
          <Plus size={16} />
          {t('schema.table.addAction')}
        </button>
      </div>
    );
  }
  return (
    <div className="canvas-page-break">
      <Workspace project={project} readOnly={readOnly} toolsMenuSlot={toolsMenuSlot} />
    </div>
  );
}

function ProjectTablesCard() {
  const t = useT();
  const { schema } = useSchemaStore();
  return (
    <div className="card">
      <h2>{t('project.tables.title')}</h2>
      {schema.tables.length === 0 ? (
        <p className="hint">{t('project.tables.empty')}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>{t('project.tables.column.name')}</th>
              <th>{t('project.tables.column.schema')}</th>
              <th className="numeric">{t('project.tables.column.columns')}</th>
              <th className="numeric">{t('project.tables.column.pk')}</th>
              <th className="numeric">{t('project.tables.column.fk')}</th>
              <th className="numeric">{t('project.tables.column.indexes')}</th>
            </tr>
          </thead>
          <tbody>
            {schema.tables.map((table) => (
              <tr key={table.id}>
                <td>{table.name}</td>
                <td>{table.schema}</td>
                <td className="numeric">{table.columns.length}</td>
                <td className="numeric">{table.primaryKey ? table.primaryKey.columns.length : 0}</td>
                <td className="numeric">{table.foreignKeys.length}</td>
                <td className="numeric">{table.indexes.length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** E1-US8 — k prázdnému projektu lze připojení doplnit kdykoliv později. */
function ConnectionCard({ project, onChanged }: { project: ProjectFile; onChanged: () => void }) {
  const t = useT();
  const [draft, setDraft] = useState<ConnectionDraft>(emptyConnectionDraft);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const attach = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.attachConnection(project.id, toConnectionInput(project.dialect, draft));
      setSaved(true);
      setEditing(false);
      onChanged();
    } catch (caught) {
      setError(describeError(caught, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <h2>{t('project.connection.title')}</h2>

      {project.connection ? (
        <p className="hint">{describeConnection(project)}</p>
      ) : (
        <div className="stack">
          <div className="notice">{t('project.connection.none')}</div>
          <p className="hint">{t('project.connection.noneHint')}</p>
        </div>
      )}

      {saved && <div className="notice notice--success">{t('project.connection.attached')}</div>}
      {error && <div className="notice notice--error">{error}</div>}

      {editing ? (
        <div className="stack" style={{ marginTop: 'var(--space-3)' }}>
          <ConnectionForm dialect={project.dialect} draft={draft} onChange={setDraft} />
          <div className="modal__actions">
            <button type="button" onClick={() => setEditing(false)}>
              <X size={16} />
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="primary"
              disabled={busy || !isConnectionDraftComplete(project.dialect, draft)}
              onClick={() => void attach()}
            >
              <Plug size={16} />
              {t('project.connection.attach')}
            </button>
          </div>
        </div>
      ) : (
        <button type="button" style={{ marginTop: 'var(--space-3)' }} onClick={() => setEditing(true)}>
          <Plug size={16} />
          {t('project.connection.attach')}
        </button>
      )}
    </div>
  );
}

/** Popis připojení bez jakýchkoliv citlivých údajů — heslo se nikdy nezobrazuje. */
function describeConnection(project: ProjectFile): string {
  const config = project.connection?.config;
  if (!config) return '';
  return config.kind === 'postgresql'
    ? `${config.user}@${config.host}:${config.port}/${config.database} (SSL: ${config.sslMode})`
    : config.filePath;
}
